import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, Star, Users, Sparkles, LogOut, Flame, Award, 
  ChevronRight, Calendar, HelpCircle, FolderOpen, Target, 
  Briefcase, CheckCircle, Brain, MapPin, ArrowLeft, Zap, TrendingUp
} from 'lucide-react';

// YAS Design V2: "Street Smart"
// More geometric, urban-inspired with bold shapes
// Playful: Stacked cards, bold borders, geometric patterns

const YAS_LOGO = '/yas-logo.png';

const YASDesignV2 = () => {
  const navigate = useNavigate();

  const colors = {
    teal: '#1B2D4F',
    tealLight: '#2A8A9D',
    tealDark: '#145566',
    orange: '#E86C3D',
    orangeLight: '#FF8A5C',
    cream: '#FAF7F2',
    warmGray: '#F5F0E8',
    navy: '#1A3A4A',
    gold: '#D4A853',
  };

  return (
    <div className="min-h-screen pb-24" style={{ 
      backgroundColor: colors.cream,
      fontFamily: "'Quicksand', sans-serif"
    }}>
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-50 bg-white rounded-xl p-2.5 border-2 shadow-md hover:shadow-lg transition-shadow"
        style={{ borderColor: colors.teal }}
      >
        <ArrowLeft className="w-5 h-5" style={{ color: colors.teal }} />
      </button>

      {/* Theme Label */}
      <div 
        className="fixed top-4 right-4 z-50 text-white px-4 py-2 rounded-xl font-bold text-sm border-2 border-white/30"
        style={{ backgroundColor: colors.navy }}
      >
        YAS V2: Street Smart
      </div>

      {/* Hero Header */}
      <div className="relative overflow-hidden" style={{ backgroundColor: colors.navy }}>
        {/* Geometric pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        />
        
        {/* Accent shapes */}
        <div 
          className="absolute top-0 right-0 w-48 h-48"
          style={{ 
            background: colors.orange,
            clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
            opacity: 0.9
          }}
        />
        <div 
          className="absolute bottom-0 left-0 w-32 h-32"
          style={{ 
            background: colors.teal,
            clipPath: 'polygon(0 100%, 100% 100%, 0 0)',
            opacity: 0.8
          }}
        />
        
        <div className="relative px-4 pt-16 pb-12">
          <div className="max-w-md mx-auto">
            {/* Logo Bar */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div 
                  className="w-14 h-14 bg-white rounded-xl flex items-center justify-center border-2 overflow-hidden"
                  style={{ borderColor: colors.orange }}
                >
                  <img src={YAS_LOGO} alt="YAS" className="w-12 h-12 object-contain" />
                </div>
                <div className="text-white">
                  <p className="text-xs font-bold opacity-70 tracking-wider">YOUTH 2 ADULT SUCCESS</p>
                  <p className="text-sm font-bold tracking-wide">SUMMER 2026</p>
                </div>
              </div>
              <button 
                className="rounded-xl p-3 border-2 border-white/30 hover:bg-white/10 transition-colors"
              >
                <LogOut className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Welcome Section - Left aligned, bold */}
            <div className="mb-6">
              <div className="flex items-center gap-4">
                <div 
                  className="w-20 h-20 rounded-2xl bg-white flex items-center justify-center border-4"
                  style={{ borderColor: colors.orange }}
                >
                  <span className="text-4xl font-black" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>B</span>
                </div>
                <div className="text-white">
                  <p className="text-xs font-bold opacity-60 tracking-widest mb-1">WHAT'S GOOD</p>
                  <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    BRANDON
                  </h1>
                  <div className="flex items-center gap-2 mt-2">
                    <span 
                      className="text-xs font-bold px-3 py-1 rounded-lg"
                      style={{ backgroundColor: colors.orange, color: 'white' }}
                    >
                      UpNEXT
                    </span>
                    <span 
                      className="text-xs font-bold px-3 py-1 rounded-lg"
                      style={{ backgroundColor: colors.teal, color: 'white' }}
                    >
                      LVL 7
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Worksite - Badge style */}
            <div 
              className="flex items-center gap-3 bg-white/10 backdrop-blur rounded-xl p-3 border border-white/20"
            >
              <MapPin className="w-5 h-5" style={{ color: colors.orange }} />
              <div className="text-white">
                <p className="text-xs font-bold opacity-60">LOCATION</p>
                <p className="font-bold text-sm">Newark Public Library</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Overlapping the header */}
      <div className="max-w-md mx-auto px-4 -mt-6 relative z-10">
        <div className="grid grid-cols-2 gap-3">
          {/* Points Card */}
          <div 
            className="bg-white rounded-2xl p-5 border-2 shadow-lg"
            style={{ borderColor: colors.orange }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-5 h-5" style={{ color: colors.orange }} fill="currentColor" />
              <span className="text-xs font-bold tracking-wider" style={{ color: colors.navy }}>POINTS</span>
            </div>
            <p className="text-4xl font-black" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>
              1,250
            </p>
          </div>

          {/* Streak Card */}
          <div 
            className="bg-white rounded-2xl p-5 border-2 shadow-lg"
            style={{ borderColor: colors.teal }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Flame className="w-5 h-5" style={{ color: colors.teal }} />
              <span className="text-xs font-bold tracking-wider" style={{ color: colors.navy }}>STREAK</span>
            </div>
            <p className="text-4xl font-black" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>
              7<span className="text-xl text-gray-400 ml-1">d</span>
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        
        {/* Quick Actions Grid */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: Target, label: 'Goals', color: colors.teal },
            { icon: Briefcase, label: 'Project', color: colors.orange },
            { icon: Trophy, label: 'Ranks', color: colors.gold },
            { icon: Award, label: 'Wins', color: colors.teal },
            { icon: FolderOpen, label: 'Files', color: colors.orange },
            { icon: HelpCircle, label: 'Help', color: colors.navy },
          ].map((item) => (
            <button
              key={item.label}
              className="bg-white rounded-xl p-4 border-2 border-transparent hover:border-current transition-all shadow-sm hover:shadow-md"
              style={{ color: item.color }}
            >
              <item.icon className="w-6 h-6 mx-auto mb-2" />
              <p className="text-xs font-bold text-center" style={{ color: colors.navy }}>{item.label}</p>
            </button>
          ))}
        </div>

        {/* Work Journal Card */}
        <div 
          className="rounded-2xl p-6 relative overflow-hidden border-2"
          style={{ backgroundColor: colors.orange, borderColor: colors.navy }}
        >
          {/* Corner accent */}
          <div 
            className="absolute top-0 right-0 w-16 h-16"
            style={{ 
              background: colors.navy,
              clipPath: 'polygon(100% 0, 0 0, 100% 100%)'
            }}
          />
          <div className="absolute top-2 right-2">
            <TrendingUp className="w-4 h-4 text-white" />
          </div>
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center border-2"
                style={{ backgroundColor: 'white', borderColor: colors.navy }}
              >
                <Sparkles className="w-6 h-6" style={{ color: colors.orange }} />
              </div>
              <div className="text-white">
                <h2 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  WORK JOURNAL
                </h2>
                <p className="text-white/80 text-sm font-semibold">Share your highlights</p>
              </div>
            </div>
            <button 
              className="w-full rounded-xl py-4 font-black text-lg border-2 hover:bg-gray-50 transition-all"
              style={{ 
                backgroundColor: 'white', 
                color: colors.navy,
                borderColor: colors.navy,
                fontFamily: "'Fredoka', sans-serif"
              }}
            >
              DROP A WIN +50
            </button>
          </div>
        </div>

        {/* This Week Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5" style={{ color: colors.navy }} />
            <h2 className="text-lg font-black tracking-tight" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>
              THIS WEEK
            </h2>
          </div>

          <div className="space-y-3">
            {/* Assessment Card */}
            <div 
              className="rounded-xl p-4 cursor-pointer border-2 hover:shadow-md transition-shadow"
              style={{ backgroundColor: colors.teal, borderColor: colors.navy }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>SKILLS CHECK</h3>
                    <p className="text-white/80 text-xs font-semibold">+50 POINTS</p>
                  </div>
                </div>
                <span 
                  className="text-xs font-black px-3 py-1 rounded-lg"
                  style={{ backgroundColor: colors.orange, color: 'white' }}
                >
                  NEW
                </span>
              </div>
            </div>

            {/* Check-in Card */}
            <div 
              className="bg-white rounded-xl p-4 cursor-pointer border-2 hover:shadow-md transition-shadow"
              style={{ borderColor: colors.warmGray }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${colors.teal}15` }}
                  >
                    <CheckCircle className="w-5 h-5" style={{ color: colors.teal }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>WEEKLY CHECK-IN</h3>
                    <p className="text-xs font-semibold" style={{ color: colors.tealLight }}>+20 POINTS</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5" style={{ color: colors.tealLight }} />
              </div>
            </div>

            {/* Trivia Card */}
            <div 
              className="bg-white rounded-xl p-4 cursor-pointer border-2 hover:shadow-md transition-shadow"
              style={{ borderColor: colors.warmGray }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${colors.orange}15` }}
                  >
                    <Zap className="w-5 h-5" style={{ color: colors.orange }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>TRIVIA TIME</h3>
                    <p className="text-xs font-semibold" style={{ color: colors.tealLight }}>+25 POINTS</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5" style={{ color: colors.tealLight }} />
              </div>
            </div>
          </div>
        </div>

        {/* Team Card */}
        <div 
          className="bg-white rounded-2xl p-5 border-2 relative overflow-hidden"
          style={{ borderColor: colors.navy }}
        >
          <div 
            className="absolute bottom-0 right-0 w-24 h-24"
            style={{ 
              background: `${colors.teal}10`,
              clipPath: 'polygon(100% 100%, 0 100%, 100% 0)'
            }}
          />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: colors.navy }}
                >
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-black" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>TEAM ALPHA</h3>
                  <p className="text-sm font-semibold" style={{ color: colors.tealLight }}>8 members</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black" style={{ color: colors.navy, fontFamily: "'Fredoka', sans-serif" }}>4,500</p>
                <p className="text-xs font-bold" style={{ color: colors.tealLight }}>PTS</p>
              </div>
            </div>
            <button 
              className="w-full rounded-xl py-3 font-bold border-2 transition-colors hover:bg-gray-50"
              style={{ 
                borderColor: colors.navy, 
                color: colors.navy,
                fontFamily: "'Fredoka', sans-serif"
              }}
            >
              VIEW SQUAD <ChevronRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div 
        className="fixed bottom-0 left-0 right-0 bg-white border-t-2 px-4 py-2"
        style={{ borderColor: colors.navy }}
      >
        <div className="max-w-md mx-auto flex justify-around items-center">
          {[
            { icon: Target, label: 'Home', active: true },
            { icon: Target, label: 'Goals', active: false },
            { icon: Trophy, label: 'Stats', active: false },
            { icon: Users, label: 'Profile', active: false },
          ].map((item) => (
            <button 
              key={item.label} 
              className="flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all"
              style={{ 
                backgroundColor: item.active ? colors.navy : 'transparent',
                color: item.active ? 'white' : colors.navy
              }}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs font-bold">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default YASDesignV2;
